import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { fetchDataverseToken, reinitializeDataverseClient } from '../api';
import { useAuth } from './AuthProvider';

export type OrgId = 'prod' | 'dev';

interface OrgContextValue {
  activeOrg: OrgId;
  setActiveOrg: (org: OrgId) => void;
  orgLabel: string;
  prodUrl: string;
  devUrl: string;
  hasDevOrg: boolean;
  /**
   * Which orgs the CURRENT BFF can actually serve (token scope + CSP
   * allowance). The env switcher hides unsupported orgs; e.g. the dev App
   * Service serves only the dev org, so "Production" is not offered there.
   * Defaults to both when the BFF doesn't report (older builds).
   */
  orgSupported: Record<OrgId, boolean>;
}

const OrgContext = createContext<OrgContextValue | null>(null);

export function useOrgContext(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrgContext must be used within DataverseOrgProvider');
  return ctx;
}

function getStorageKey(appId?: string): string {
  return appId ? `vera-${appId}-active-org` : 'vera-active-org';
}

/**
 * Resolve the org that DataverseOrgProvider will activate for `appId`,
 * without rendering it. Safe to call at module/App scope (org switching does
 * a full page reload, so the value is stable for the life of the page).
 * Used to scope the persisted query cache per org.
 */
export function getStoredOrgId(appId?: string, defaultOrg: OrgId = 'prod'): OrgId {
  const prodUrl = import.meta.env.VITE_DATAVERSE_URL ?? '';
  const devUrl = import.meta.env.VITE_DATAVERSE_URL_DEV ?? '';
  const hasDevOrg = Boolean(devUrl) && devUrl !== prodUrl;
  const stored = localStorage.getItem(getStorageKey(appId));
  if (stored === 'dev' && hasDevOrg) return 'dev';
  if (stored === 'prod') return 'prod';
  return hasDevOrg ? defaultOrg : 'prod';
}

function getOrgLabel(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\.crm.*$/, '');
}

interface DataverseOrgProviderProps {
  children: ReactNode;
  /** App identifier used to persist org selection per-app (e.g. 'sales', 'catalog') */
  appId?: string;
  /** Which org to default to when no selection is stored. Defaults to 'prod'. */
  defaultOrg?: OrgId;
}

export function DataverseOrgProvider({ children, appId, defaultOrg = 'prod' }: DataverseOrgProviderProps) {
  const prodUrl = import.meta.env.VITE_DATAVERSE_URL ?? '';
  const devUrl = import.meta.env.VITE_DATAVERSE_URL_DEV ?? '';
  const hasDevOrg = Boolean(devUrl) && devUrl !== prodUrl;
  const storageKey = getStorageKey(appId);

  const [activeOrg] = useState<OrgId>(() => getStoredOrgId(appId, defaultOrg));

  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [clientReady, setClientReady] = useState(false);

  // Which client org ids this BFF can serve: an org is supported when its
  // baked URL matches one of the BFF's configured Dataverse URLs. Unknown
  // (older BFF without the field) means assume supported.
  const normalize = (u: string) => u.trim().toLowerCase().replace(/\/+$/, '');
  const bffUrls = user?.dataverseOrgs
    ? [user.dataverseOrgs.primary, user.dataverseOrgs.dev, user.dataverseOrgs.prod]
        .filter(Boolean)
        .map((u) => normalize(u as string))
    : null;
  const orgSupported: Record<OrgId, boolean> = {
    prod: !bffUrls || bffUrls.includes(normalize(prodUrl)),
    dev: !bffUrls || bffUrls.includes(normalize(devUrl)),
  };

  // Token cache shared across all Dataverse calls for the active org
  const cachedToken = useRef<string | null>(null);
  const tokenExpiresAt = useRef<Date | null>(null);
  const refreshPromise = useRef<Promise<string> | null>(null);

  const createTokenProvider = useCallback(
    (org: OrgId) => {
      // Clear cache when org changes
      cachedToken.current = null;
      tokenExpiresAt.current = null;
      refreshPromise.current = null;

      return async (): Promise<string> => {
        // Return cached token if still fresh (>5 min remaining)
        const bufferMs = 5 * 60 * 1000;
        if (
          cachedToken.current &&
          tokenExpiresAt.current &&
          tokenExpiresAt.current.getTime() - Date.now() > bufferMs
        ) {
          return cachedToken.current;
        }

        // If a refresh is already in flight, wait for it (mutex)
        if (refreshPromise.current) {
          return refreshPromise.current;
        }

        // Start a new refresh; all concurrent callers share this promise.
        refreshPromise.current = (async () => {
          try {
            // fetchDataverseToken routes a 401 through bffFetch's teardown
            // (prompted re-login). The bare `/auth/login` redirect that lived
            // here silent-SSO'd a fresh session and made the idle timeout
            // invisible (the 8/26 prod report).
            const { accessToken, expiresOn } = await fetchDataverseToken(org);
            cachedToken.current = accessToken;
            tokenExpiresAt.current = expiresOn ? new Date(expiresOn) : null;
            return accessToken;
          } finally {
            refreshPromise.current = null;
          }
        })();

        return refreshPromise.current;
      };
    },
    [],
  );

  const setActiveOrg = useCallback(
    (org: OrgId) => {
      if (org === activeOrg) return;
      localStorage.setItem(storageKey, org);
      // Full page reload ensures clean state — new token, fresh queries, no stale cache
      window.location.reload();
    },
    [activeOrg, storageKey],
  );

  // Self-heal a stored org this BFF can't serve: without this, a persisted
  // "Production" preference on the dev App Service leaves every Dataverse
  // call CSP-blocked on every reload with no way out but clearing storage.
  const healed = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || healed.current) return;
    const other: OrgId = activeOrg === 'prod' ? 'dev' : 'prod';
    if (!orgSupported[activeOrg] && orgSupported[other]) {
      healed.current = true;
      console.warn(`[org] stored org "${activeOrg}" is not served by this BFF; switching to "${other}".`);
      localStorage.setItem(storageKey, other);
      window.location.reload();
    }
  }, [isAuthenticated, activeOrg, orgSupported, storageKey]);

  // Initialize client for the active org — must complete before children render
  // to prevent React Query hooks from firing before the client is ready
  useEffect(() => {
    if (!isAuthenticated) {
      setClientReady(false);
      return;
    }

    const targetUrl = activeOrg === 'dev' && hasDevOrg ? devUrl : prodUrl;
    reinitializeDataverseClient(targetUrl, createTokenProvider(activeOrg), activeOrg);
    setClientReady(true);
  }, [isAuthenticated, activeOrg, hasDevOrg, devUrl, prodUrl, createTokenProvider]);

  const orgLabel = activeOrg === 'dev' && hasDevOrg ? getOrgLabel(devUrl) : getOrgLabel(prodUrl);

  const value = useMemo<OrgContextValue>(
    () => ({
      activeOrg,
      setActiveOrg,
      orgLabel,
      prodUrl,
      devUrl,
      hasDevOrg,
      orgSupported,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeOrg, setActiveOrg, orgLabel, prodUrl, devUrl, hasDevOrg, orgSupported.prod, orgSupported.dev],
  );

  // Don't render children until the Dataverse client is initialized —
  // prevents React Query hooks from firing before the client is ready.
  // Exception: when the user is unauthenticated, render children anyway so
  // the router can navigate to /login (no Dataverse queries fire there).
  if (!clientReady && (authLoading || isAuthenticated)) return null;

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}
